import React, { useState, useMemo, useCallback } from "react";
import { Plus, Trash2, Percent } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";

import type { SilaeFormData, SilaeSourceFormData } from "@/types/silae";
import { createEmptySource } from "@/hooks/useSilaeWizardFormik";
import { SourceItemConfig } from "./SourceItemConfig";

interface TauxSourceConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string; // Ex: "ifBranch.bonuses.0"
  title?: string;
  description?: string;
  className?: string;
}

export const TauxSourceConfig: React.FC<TauxSourceConfigProps> = ({
  formik,
  fieldPrefix,
  title,
  description,
  className,
}) => {
  const { t } = useTranslation();

  // Utiliser les traductions par défaut si title et description ne sont pas fournis
  const finalTitle = title || t("tauxSourceConfig.title");
  const finalDescription = description || t("tauxSourceConfig.description");

  // État pour l'onglet actif
  const [activeTab, setActiveTab] = useState("taux-0");

  // Chemin complet vers le tableau des taux
  const tauxArrayPath = `${fieldPrefix}.tauxs`;

  // Récupérer les taux actuels
  const tauxs: SilaeSourceFormData[] = useMemo(
    () => getIn(formik.values, tauxArrayPath) || [],
    [formik.values, tauxArrayPath],
  );

  // Fonction pour vérifier si un taux a des erreurs
  const hasTauxErrors = useCallback(
    (index: number): boolean => {
      const tauxErrors = getIn(formik.errors, `${tauxArrayPath}.${index}`);
      if (!tauxErrors) return false;
      if (typeof tauxErrors === "string") return true;
      if (typeof tauxErrors === "object") {
        return Object.keys(tauxErrors).length > 0;
      }
      return false;
    },
    [formik.errors, tauxArrayPath],
  );

  // Ajouter un nouveau taux
  const addTaux = useCallback(() => {
    const newTaux = createEmptySource();
    const updatedTauxs = [...tauxs, newTaux];

    // Si on a plusieurs taux (updatedTauxs.length > 1), les conditions sont obligatoires
    // Si on a un seul taux, les conditions sont optionnelles (garder la valeur actuelle)
    const hasMultipleTauxs = updatedTauxs.length > 1;

    // Mettre à jour hasConditions pour tous les taux
    const updatedTauxsWithConditions = updatedTauxs.map((taux) => ({
      ...taux,
      hasConditions: hasMultipleTauxs ? true : (taux.hasConditions ?? false),
    }));

    formik.setFieldValue(tauxArrayPath, updatedTauxsWithConditions);
    setActiveTab(`taux-${updatedTauxs.length - 1}`);
  }, [tauxs, formik, tauxArrayPath]);

  // Supprimer un taux
  const removeTaux = useCallback(
    (index: number) => {
      if (tauxs.length <= 1) return;
      const updatedTauxs = tauxs.filter((_, i) => i !== index);

      // Si on a plusieurs taux (updatedTauxs.length > 1), les conditions sont obligatoires
      // Si on a un seul taux, les conditions sont optionnelles (garder la valeur actuelle)
      const hasMultipleTauxs = updatedTauxs.length > 1;

      // Mettre à jour hasConditions pour tous les taux
      const updatedTauxsWithConditions = updatedTauxs.map((taux) => ({
        ...taux,
        hasConditions: hasMultipleTauxs ? true : (taux.hasConditions ?? false),
      }));

      formik.setFieldValue(tauxArrayPath, updatedTauxsWithConditions);
      if (activeTab === `taux-${index}`) {
        setActiveTab("taux-0");
      }
    },
    [tauxs, formik, tauxArrayPath, activeTab],
  );

  return (
    <Card className={`bg-background dark:bg-background mb-0 ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`text-lg`}>{finalTitle}</CardTitle>
            <CardDescription>{finalDescription}</CardDescription>
          </div>
          {tauxs.length < 5 && (
            <Button
              type="button"
              onClick={addTaux}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {t("tauxSourceConfig.addTaux")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {tauxs.length === 1 ? (
          // Un seul taux : affichage direct sans tabs
          <SourceItemConfig
            formik={formik}
            sourceFieldPrefix={`${tauxArrayPath}.0`}
            totalSources={tauxs.length}
            sourceIndex={0}
          />
        ) : (
          // Plusieurs taux : utiliser les tabs
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
              {tauxs.map((taux, index) => (
                <div key={taux.id} className="flex items-center gap-1">
                  <TabsTrigger
                    value={`taux-${index}`}
                    className={`flex items-center gap-2 ${
                      hasTauxErrors(index) ? "border-2 border-red-500" : ""
                    }`}
                  >
                    <Percent className="h-3 w-3" />
                    {t("tauxSourceConfig.taux")} {index + 1}
                    {tauxs.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTaux(index);
                        }}
                        className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10 hover:text-destructive"
                      >
                        <Trash2 className="h-2 w-2" />
                      </Button>
                    )}
                  </TabsTrigger>
                </div>
              ))}
            </TabsList>

            {tauxs.map((_, index) => (
              <TabsContent key={index} value={`taux-${index}`}>
                <SourceItemConfig
                  formik={formik}
                  sourceFieldPrefix={`${tauxArrayPath}.${index}`}
                  totalSources={tauxs.length}
                  sourceIndex={index}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};
